import { l as DocContext, t as textRendererStyle, k as jsxRuntimeExports, n as DynamicMath } from "./doenetml-drNYjJiF.js";
import React__default, { useContext, useRef } from "react";
import { r as removeJXGEventHandlers, j as jxg, s as syncLayer, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-BcR36ozd.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { BoardContext, POINT_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { c as characterizeOffGraphPoint } from "./offGraphIndicators-CYoqQHuD.js";
import { n as normalizePointStyle, a as normalizePointSize, b as adjustPointLabelPosition, e as calculatePointLabelAnchor } from "./graph-W6vrFcBu.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { c as resolveMarkerColor } from "./styleColors-Cknqcr0q.js";
import { a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-tcr76K44.js";
const point = React__default.memo(function Point(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Point.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  let pointJXG = useRef(null);
  let shadowPointJXG = useRef(null);
  const dragState = usePointerDragState();
  let previousWithLabel = useRef(null);
  let previousLabelPosition = useRef(null);
  let calculatedX = useRef(null);
  let calculatedY = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  let lastPositionFromCore = useRef(null);
  let offGraphIndicator = useRef([0, 0]);
  let nearEdgeOfGraph = useRef([0, 0]);
  let fixed = useRef(false);
  let fixLocation = useRef(false);
  let switchable = useRef(false);
  fixed.current = SVs.fixed;
  fixLocation.current = !SVs.draggable || SVs.fixLocation || SVs.fixed;
  switchable.current = SVs.switchable && !SVs.fixed;
  const { darkMode = void 0 } = useContext(DocContext);
  const useOpenSymbol = SVs.open || SVs.open === void 0 && SVs.selectedStyle.markerFilled === false || ["cross", "plus"].includes(SVs.selectedStyle.markerStyle);
  useBoardPointerTracking(board, dragState);
  React__default.useEffect(() => {
    return () => {
      if (pointJXG.current !== null && shadowPointJXG.current !== null) {
        removeJXGEventHandlers(shadowPointJXG.current);
        board.removeObject(pointJXG.current);
        board.removeObject(shadowPointJXG.current);
        pointJXG.current = null;
        shadowPointJXG.current = null;
      }
    };
  }, []);
  function createPointJXG() {
    if (board === null) {
      return null;
    }
    const markerColor = resolveMarkerColor(SVs.selectedStyle, darkMode);
    let fillColor = useOpenSymbol ? "var(--canvas)" : markerColor;
    let strokeColor = useOpenSymbol ? markerColor : "none";
    let withlabel = SVs.labelForGraph !== "";
    let jsxPointAttributes = {
      name: SVs.labelForGraph,
      visible: !SVs.hidden,
      withlabel,
      fixed: true,
      layer: 10 * SVs.layer + POINT_LAYER_OFFSET,
      fillColor,
      strokeColor,
      strokeOpacity: SVs.selectedStyle.markerOpacity,
      fillOpacity: SVs.selectedStyle.markerOpacity,
      highlightFillColor: "var(--mainGray)",
      highlightStrokeColor: "var(--lightBlue)",
      size: normalizePointSize(
        SVs.selectedStyle.markerSize,
        SVs.selectedStyle.markerStyle
      ),
      face: normalizePointStyle(
        SVs.selectedStyle.markerStyle,
        offGraphIndicator.current
      ),
      highlight: !fixLocation.current
    };
    if (withlabel) {
      let labelPosition = adjustPointLabelPosition(
        SVs.labelPosition,
        nearEdgeOfGraph.current
      );
      previousLabelPosition.current = labelPosition;
      let { offset, anchorx, anchory } = calculatePointLabelAnchor(labelPosition);
      jsxPointAttributes.label = {
        offset,
        anchorx,
        anchory,
        highlight: false,
        ...computeLabelMaskCssStyle({
          layer: SVs.layer,
          masked: SVs.maskLabel
        }),
        highlightStrokeOpacity: 1
      };
      if (SVs.labelHasLatex) {
        jsxPointAttributes.label.useMathJax = true;
      }
      if (SVs.applyStyleToLabel) {
        jsxPointAttributes.label.strokeColor = markerColor;
      } else {
        jsxPointAttributes.label.strokeColor = "var(--canvasText)";
      }
    } else {
      jsxPointAttributes.label = {
        highlight: false,
        ...computeLabelMaskCssStyle({
          layer: SVs.layer,
          masked: SVs.maskLabel
        }),
        highlightStrokeOpacity: 1
      };
      if (SVs.labelHasLatex) {
        jsxPointAttributes.label.useMathJax = true;
      }
    }
    if (fixLocation.current) {
      jsxPointAttributes.showInfoBox = false;
    } else {
      jsxPointAttributes.showInfoBox = SVs.showCoordsWhenDragging;
    }
    let coords = [
      lastPositionFromCore.current?.[0] ?? 0,
      lastPositionFromCore.current?.[1] ?? 0
    ];
    if (!Number.isFinite(coords[0])) {
      coords[0] = 0;
      jsxPointAttributes["visible"] = false;
    }
    if (!Number.isFinite(coords[1])) {
      coords[1] = 0;
      jsxPointAttributes["visible"] = false;
    }
    let shadowPointAttributes = { ...jsxPointAttributes };
    shadowPointAttributes.fixed = fixed.current;
    shadowPointAttributes.showInfoBox = false;
    shadowPointAttributes.withlabel = false;
    shadowPointAttributes.fillOpacity = 0;
    shadowPointAttributes.strokeOpacity = 0;
    shadowPointAttributes.highlightFillOpacity = 0;
    shadowPointAttributes.highlightStrokeOpacity = 0;
    let newShadowPointJXG = board.create(
      "point",
      coords,
      shadowPointAttributes
    );
    newShadowPointJXG.isDraggable = !fixLocation.current;
    let newPointJXG = board.create(
      "point",
      coords,
      jsxPointAttributes
    );
    attachLabelHoverHighlight({
      hoverTargetJXG: newShadowPointJXG,
      getLabelJXG: () => pointJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    attachLineFamilyDragHandlers({
      jxg: newShadowPointJXG,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      actions: {
        move: actions.movePoint,
        focus: actions.pointFocused,
        click: actions.pointClicked,
        clickPrelude: actions.switchPoint
      },
      clickPreludeGate: switchable,
      snapshot: () => [
        ...newShadowPointJXG.coords.scrCoords
      ],
      dispatchTransientBelowThreshold: true,
      buildTransientMoveArgs: (e, snap) => {
        let viaPointer = e.type === "pointermove";
        let [xMin, yMax, xMax, yMin] = board.getBoundingBox();
        let xminAdjusted = xMin;
        let xmaxAdjusted = xMax;
        let yminAdjusted = yMin;
        let ymaxAdjusted = yMax;
        if (xMax < xMin) {
          [xmaxAdjusted, xminAdjusted] = [xminAdjusted, xmaxAdjusted];
        }
        if (yMax < yMin) {
          [ymaxAdjusted, yminAdjusted] = [yminAdjusted, ymaxAdjusted];
        }
        let xscale = xmaxAdjusted - xminAdjusted;
        let yscale = ymaxAdjusted - yminAdjusted;
        xmaxAdjusted -= xscale * 0.01;
        xminAdjusted += xscale * 0.01;
        ymaxAdjusted -= yscale * 0.01;
        yminAdjusted += yscale * 0.01;
        if (viaPointer && snap && dragState.pointerAtDown.current) {
          [calculatedX.current, calculatedY.current] = pointerEventToUserCoords(
            e,
            dragState.pointerAtDown.current,
            snap,
            board
          );
        } else {
          calculatedX.current = newShadowPointJXG.X();
          calculatedY.current = newShadowPointJXG.Y();
        }
        calculatedX.current = Math.min(
          xmaxAdjusted,
          Math.max(xminAdjusted, calculatedX.current || 0)
        );
        calculatedY.current = Math.min(
          ymaxAdjusted,
          Math.max(yminAdjusted, calculatedY.current || 0)
        );
        let shadowX = Math.min(
          xmaxAdjusted,
          Math.max(xminAdjusted, newShadowPointJXG.X())
        );
        let shadowY = Math.min(
          ymaxAdjusted,
          Math.max(yminAdjusted, newShadowPointJXG.Y())
        );
        newShadowPointJXG.coords.setCoordinates(jxg.COORDS_BY_USER, [
          shadowX,
          shadowY
        ]);
        return {
          x: calculatedX.current,
          y: calculatedY.current,
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => ({
        x: calculatedX.current,
        y: calculatedY.current
      }),
      onDragApplied: () => {
        newPointJXG.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastPositionFromCore.current
        );
        board.updateInfobox(newPointJXG);
      },
      onDownExtra: () => {
        if (shadowPointJXG.current != null && pointJXG.current != null) {
          shadowPointJXG.current.visProp.highlightfillopacity = pointJXG.current.visProp.fillopacity;
          shadowPointJXG.current.visProp.highlightstrokeopacity = pointJXG.current.visProp.strokeopacity;
        }
      },
      onUpExtra: () => {
        if (shadowPointJXG.current != null && pointJXG.current != null) {
          shadowPointJXG.current.visProp.highlightfillopacity = 0;
          shadowPointJXG.current.visProp.highlightstrokeopacity = 0;
        }
      },
      onHitExtra: () => {
        board.updateInfobox(pointJXG.current);
      }
    });
    pointJXG.current = newPointJXG;
    shadowPointJXG.current = newShadowPointJXG;
    previousWithLabel.current = withlabel;
  }
  if (board) {
    lastPositionFromCore.current = [...SVs.numericalXs];
    offGraphIndicator.current = [0, 0];
    if (!SVs.hideOffGraphIndicator) {
      let { needIndicator, indicatorCoords, indicatorSides } = characterizeOffGraphPoint(lastPositionFromCore.current, board);
      if (needIndicator) {
        lastPositionFromCore.current = indicatorCoords;
        offGraphIndicator.current = indicatorSides;
      }
    }
    nearEdgeOfGraph.current = [0, 0];
    let flippedX = false;
    let flippedY = false;
    let [xMin, yMax, xMax, yMin] = board.getBoundingBox();
    if (xMax < xMin) {
      flippedX = true;
      [xMax, xMin] = [xMin, xMax];
    }
    if (yMax < yMin) {
      flippedY = true;
      [yMax, yMin] = [yMin, yMax];
    }
    let xscale = xMax - xMin;
    let yscale = yMax - yMin;
    let xminAdjusted = xMin + xscale * 0.05;
    let xmaxAdjusted = xMax - xscale * 0.05;
    let yminAdjusted = yMin + yscale * 0.05;
    let ymaxAdjusted = yMax - yscale * 0.05;
    if (Number.isFinite(lastPositionFromCore.current[0]) && Number.isFinite(lastPositionFromCore.current[1])) {
      if (lastPositionFromCore.current[0] < xminAdjusted) {
        nearEdgeOfGraph.current[0] = flippedX ? 1 : -1;
      } else if (lastPositionFromCore.current[0] > xmaxAdjusted) {
        nearEdgeOfGraph.current[0] = flippedX ? -1 : 1;
      }
      if (lastPositionFromCore.current[1] < yminAdjusted) {
        nearEdgeOfGraph.current[1] = flippedY ? 1 : -1;
      } else if (lastPositionFromCore.current[1] > ymaxAdjusted) {
        nearEdgeOfGraph.current[1] = flippedY ? -1 : 1;
      }
    }
    if (pointJXG.current === null) {
      createPointJXG();
    } else {
      const markerColor = resolveMarkerColor(SVs.selectedStyle, darkMode);
      let fillColor = useOpenSymbol ? "var(--canvas)" : markerColor;
      let strokeColor = useOpenSymbol ? markerColor : "none";
      if (pointJXG.current.visProp.fillcolor !== fillColor) {
        pointJXG.current.visProp.fillcolor = fillColor;
      }
      let x = lastPositionFromCore.current?.[0];
      let y = lastPositionFromCore.current?.[1];
      pointJXG.current.coords.setCoordinates(jxg.COORDS_BY_USER, [x, y]);
      if (dragCoordination.draggedTag.current === null) {
        shadowPointJXG.current?.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          [x, y]
        );
      }
      let visible = !SVs.hidden;
      if (!shadowPointJXG.current) {
        return;
      }
      if (Number.isFinite(x) && Number.isFinite(y)) {
        let actuallyChangedVisibility = pointJXG.current.visProp["visible"] !== visible;
        pointJXG.current.visProp["visible"] = visible;
        pointJXG.current.visPropCalc["visible"] = visible;
        shadowPointJXG.current.visProp["visible"] = visible;
        shadowPointJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          pointJXG.current.setAttribute({ visible });
          shadowPointJXG.current.setAttribute({ visible });
        }
      } else {
        pointJXG.current.visProp["visible"] = false;
        pointJXG.current.visPropCalc["visible"] = false;
        shadowPointJXG.current.visProp["visible"] = false;
        shadowPointJXG.current.visPropCalc["visible"] = false;
      }
      if (syncLayer(pointJXG.current, SVs.layer, POINT_LAYER_OFFSET)) {
        shadowPointJXG.current?.setAttribute({
          layer: 10 * SVs.layer + POINT_LAYER_OFFSET
        });
      }
      pointJXG.current.visProp.highlight = !fixLocation.current;
      shadowPointJXG.current.visProp.highlight = !fixLocation.current;
      shadowPointJXG.current.visProp.fixed = fixed.current;
      shadowPointJXG.current.isDraggable = !fixLocation.current;
      if (pointJXG.current.visProp.strokecolor !== strokeColor) {
        pointJXG.current.visProp.strokecolor = strokeColor;
        shadowPointJXG.current.visProp.strokecolor = strokeColor;
        pointJXG.current.visProp.fillColor = fillColor;
        shadowPointJXG.current.visProp.fillColor = fillColor;
      }
      if (pointJXG.current.visProp.strokeopacity !== SVs.selectedStyle.markerOpacity) {
        pointJXG.current.visProp.strokeopacity = SVs.selectedStyle.markerOpacity;
        pointJXG.current.visProp.fillopacity = SVs.selectedStyle.markerOpacity;
      }
      let newFace = normalizePointStyle(
        SVs.selectedStyle.markerStyle,
        offGraphIndicator.current
      );
      if (pointJXG.current.visProp.face !== newFace) {
        pointJXG.current.setAttribute({ face: newFace });
        shadowPointJXG.current.setAttribute({ face: newFace });
      }
      let newSize = normalizePointSize(
        SVs.selectedStyle.markerSize,
        SVs.selectedStyle.markerStyle
      );
      if (pointJXG.current.visProp.size !== newSize) {
        pointJXG.current.setAttribute({ size: newSize });
        shadowPointJXG.current.setAttribute({ size: newSize });
      }
      if (fixLocation.current || offGraphIndicator.current[0] || offGraphIndicator.current[1]) {
        pointJXG.current.visProp.showinfobox = false;
        board.displayInfobox(false);
      } else {
        pointJXG.current.visProp.showinfobox = SVs.showCoordsWhenDragging;
      }
      if (shadowPointJXG.current.highlighted) {
        board.updateInfobox(pointJXG.current);
      }
      pointJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        pointJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      if (pointJXG.current.hasLabel && pointJXG.current.label) {
        const label = pointJXG.current.label;
        label.needsUpdate = true;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, markerColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        let labelPosition = adjustPointLabelPosition(
          SVs.labelPosition,
          nearEdgeOfGraph.current
        );
        if (labelPosition !== previousLabelPosition.current) {
          let { offset, anchorx, anchory } = calculatePointLabelAnchor(labelPosition);
          label.visProp.anchorx = anchorx;
          label.visProp.anchory = anchory;
          label.visProp.offset = offset;
          previousLabelPosition.current = labelPosition;
          label.fullUpdate();
        } else {
          label.update();
        }
      }
      pointJXG.current.needsUpdate = true;
      pointJXG.current.update();
      shadowPointJXG.current.needsUpdate = true;
      shadowPointJXG.current.update();
      board.updateRenderer();
    }
    return null;
  }
  if (SVs.hidden) {
    return null;
  }
  const mathJaxify = "\\(" + SVs.latex + "\\)";
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: mathJaxify }) });
});
export {
  point as default
};
